import React, { useRef } from 'react';
import { ArrowRight, Github, Linkedin, ExternalLink, Code2, Database, Server } from 'lucide-react';
import { Button } from './ui/button';
import { usePortfolio } from '../context/PortfolioContext';

const Hero = () => {
  const { portfolioData, loading } = usePortfolio();
  const primaryRef = useRef(null);
  const secondaryRef = useRef(null);
  
  if (loading || !portfolioData) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        <div className="text-orange-500 text-xl">Loading...</div>
      </section>
    );
  }
  
  const { basics } = portfolioData;

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMagnetic = (ref, event) => {
    const node = ref?.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;
    node.style.transform = `translate(${x * 0.05}px, ${y * 0.05}px) scale(1.02)`;
  };

  const resetMagnetic = (ref) => {
    const node = ref?.current;
    if (node) node.style.transform = 'translate(0px, 0px) scale(1)';
  };

  return (
    <section
      id="hero"
      data-story-section
      className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    >
      {/* Animated Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,107,53,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,107,53,0.03)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>
      
      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse-glow"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl animate-pulse-glow animation-delay-2000"></div>

      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 text-sm font-medium">
                  Available for work
                </span>
              </div>
              <div>
                <h2 className="text-orange-500 font-medium text-xl mb-2">Hello</h2>
                <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-4">
                  I'm <span className="gradient-text">{basics.name.split(' ')[0]}</span>
                </h1>
                <p className="text-3xl md:text-4xl text-gray-400 font-light">
                  {basics.label}
                </p>
              </div>
            </div>

            <p className="text-lg text-gray-400 leading-relaxed max-w-xl">
              Crafting scalable digital experiences with modern technologies. Specialized in full-stack development with Angular, React, .NET Core, and cloud architectures.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                ref={primaryRef}
                onMouseMove={(e) => handleMagnetic(primaryRef, e)}
                onMouseLeave={() => resetMagnetic(primaryRef)}
                onClick={() => scrollToSection('contact')}
                className="magnetic bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg border-0 group shadow-lg shadow-orange-500/20"
              >
                Let's Connect
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button
                ref={secondaryRef}
                onMouseMove={(e) => handleMagnetic(secondaryRef, e)}
                onMouseLeave={() => resetMagnetic(secondaryRef)}
                onClick={() => scrollToSection('experience')}
                variant="outline"
                className="magnetic border-orange-500/30 text-orange-500 hover:bg-orange-500/10 px-8 py-6 text-lg"
              >
                View Work
              </Button>
            </div>

            <div className="flex gap-4 pt-4">
              <a
                href={basics.profiles[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-xl glass hover:bg-orange-500/10 text-gray-400 hover:text-orange-500 transition-all duration-300 border border-transparent hover:border-orange-500/30"
              >
                <Linkedin size={24} />
              </a>
              <a
                href={basics.profiles[1].url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-xl glass hover:bg-orange-500/10 text-gray-400 hover:text-orange-500 transition-all duration-300 border border-transparent hover:border-orange-500/30"
              >
                <Github size={24} />
              </a>
              <a
                href={basics.profiles[2].url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-xl glass hover:bg-orange-500/10 text-gray-400 hover:text-orange-500 transition-all duration-300 border border-transparent hover:border-orange-500/30"
              >
                <ExternalLink size={24} />
              </a>
            </div>
          </div>

          {/* Right Content - 3D Illustration Area */}
          <div className="hidden lg:flex items-center justify-center relative">
            <div className="relative w-full h-[600px] bg-transparent shadow-none">
              {/* Central Character Placeholder - Will be replaced with 3D illustration */}
              <div className="absolute inset-0 flex items-center justify-center bg-transparent">
                <div className="relative bg-transparent shadow-none parallax-card">
                  {/* Main Circle */}
                  <div className="w-80 h-80 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20 backdrop-blur-xl border border-orange-500/30 flex items-center justify-center relative">
                    {/* Developer Illustration Placeholder */}
                    <div className="w-64 h-64 rounded-full bg-gradient-to-br from-zinc-900 to-zinc-800 flex items-center justify-center shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
                      <Code2 size={72} className="text-orange-500" />
                    </div>
                  </div>

                  {/* Floating Tech Icons - softened */}
                  <div className="absolute -top-12 -left-10 p-4 bg-zinc-900/70 border border-orange-500/30 rounded-full shadow-lg backdrop-blur-md animate-float">
                    <Code2 size={28} className="text-orange-400" />
                  </div>
                  <div className="absolute -top-14 -right-6 p-4 bg-zinc-900/70 border border-amber-400/30 rounded-full shadow-lg backdrop-blur-md animate-float animation-delay-1000">
                    <Database size={26} className="text-amber-300" />
                  </div>
                  <div className="absolute -bottom-12 left-6 p-4 bg-zinc-900/70 border border-orange-500/30 rounded-full shadow-lg backdrop-blur-md animate-float animation-delay-2000">
                    <Server size={26} className="text-orange-300" />
                  </div>

                  {/* Soft glow rings */}
                  <div className="absolute inset-4 rounded-full border border-orange-500/20" />
                  <div className="absolute inset-10 rounded-full border border-amber-400/10" />

                  {/* Organic blobs */}
                  <div className="absolute top-12 -right-20 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl animate-float-reverse"></div>
                  <div className="absolute bottom-16 -left-24 w-28 h-28 bg-amber-400/10 rounded-full blur-2xl animate-float"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-orange-500/30 flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-orange-500 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
